/**
 * The only place the app reads the wall clock. Everything else calls `now()`.
 *
 * In dev the tick CLI can set a fake time (`infra/tick.ts set 2026-10-01T09:00`), which the
 * Vite dev server exposes at `/__now`. The offset is applied here so the sim, the UI and
 * the save timestamps all agree.
 */

let offsetMs = 0;

export function now(): number {
  return Date.now() + offsetMs;
}

export function setFakeNow(fakeMs: number | null): void {
  offsetMs = fakeMs === null ? 0 : fakeMs - Date.now();
}

export function fakeOffsetMs(): number {
  return offsetMs;
}

/** Ask the dev server for the fake clock. No-op in production builds. */
export async function syncFakeClock(): Promise<void> {
  if (!import.meta.env.DEV) return;
  try {
    const res = await fetch('/__now', { cache: 'no-store' });
    const body = (await res.json()) as { now: string | null };
    const parsed = body.now ? Date.parse(body.now) : NaN;
    setFakeNow(Number.isFinite(parsed) ? parsed : null);
  } catch {
    setFakeNow(null);
  }
}

/** Jump the clock forward. Used by tests and the tick CLI. Exposed on window as __hedgerow.jump. */
export function jumpMs(ms: number): void {
  offsetMs += ms;
}

declare global {
  interface Window {
    /** Test and dev hooks. `store` is set by the game store once it is created. */
    __hedgerow?: { jump: (minutes: number) => void; now: () => number; store?: unknown };
  }
}

if (typeof window !== 'undefined') {
  window.__hedgerow = { jump: (minutes: number) => jumpMs(minutes * 60_000), now };
}
